'use client';

import { useCheckGiftHandle } from '@morfi/backend-sdk-v2/client';
import { useEffect, useState } from 'react';

/**
 * Possible result states for a debounced Morfi-handle lookup.
 *
 * - `idle` — input is empty or below the minimum length
 * - `checking` — request in flight
 * - `found` — handle resolves to a valid recipient who accepts gifts
 * - `not_found` — backend confirmed the handle does not exist
 * - `self` — caller's own handle
 * - `opted_out` — recipient has opted out via `gifts_disabled`
 * - `invalid_format` — failed input regex
 * - `network_error` — request errored (transient)
 */
export type MorfiHandleStatus = 'idle' | 'checking' | 'found' | 'not_found' | 'self' | 'opted_out' | 'invalid_format' | 'network_error';
export interface UseHandleCheckResult {
  status: MorfiHandleStatus;
  recipientUserId: string | null;
  giftsDisabled: boolean;
}
const HANDLE_REGEX: RegExp = /^[a-z0-9_]{1,32}$/;
const DEBOUNCE_MS = 300;
interface ApiSuccess {
  exists: boolean;
  userId?: string;
  giftsDisabled?: boolean;
}
interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}
interface CheckedHandleResult extends UseHandleCheckResult {
  handle: string;
}

// ALLOW-UNKNOWN: type guard narrowing arbitrary thrown values from the SDK.
function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

// ALLOW-UNKNOWN: SDK rejections may be Error, ApiError envelope, or raw object.
function extractErrorCode(err: unknown): string | null {
  if (!isObject(err)) return null;
  // Common shapes from SDK / fetch wrapper.
  if (typeof err.code === 'string') return err.code;
  const data = err.data;
  if (isObject(data) && typeof data.code === 'string') return data.code;
  // ALLOW-UNKNOWN: nested error envelope shape from fetchWrapperWithMeta.
  const error = (err as {
    error?: unknown;
  }).error;
  if (isObject(error)) {
    const inner = error as ApiErrorEnvelope;
    if (typeof inner.code === 'string') return inner.code;
  }
  return null;
}

/**
 * Debounced Morfi-handle validation hook.
 *
 * Calls `POST /v1/gifts/handle-check` after the input stabilises for
 * {@link DEBOUNCE_MS} ms. Returns a strongly-typed status enum so the
 * UI can render the correct affordance without leaking backend codes.
 */
export function useHandleCheck(handle: string): UseHandleCheckResult {
  const trimmed = handle.trim().toLowerCase();
  const [checked, setChecked] = useState<CheckedHandleResult | null>(null);
  const checkGiftHandle = useCheckGiftHandle();
  const mutateAsync = checkGiftHandle.mutateAsync;
  useEffect(() => {
    if (!HANDLE_REGEX.test(trimmed)) return;
    let cancelled = false;
    const timer = setTimeout((): void => {
      void (async (): Promise<void> => {
        try {
          const result = await mutateAsync({
            handle: trimmed
          });
          if (cancelled) return;
          const data = ((result as {
            data?: ApiSuccess;
          }).data ?? result) as ApiSuccess;
          if (data.exists !== true) {
            setChecked({
              handle: trimmed,
              status: 'not_found',
              recipientUserId: null,
              giftsDisabled: false
            });
            return;
          }
          if (data.giftsDisabled === true) {
            setChecked({
              handle: trimmed,
              status: 'opted_out',
              recipientUserId: data.userId ?? null,
              giftsDisabled: true
            });
            return;
          }
          setChecked({
            handle: trimmed,
            status: 'found',
            recipientUserId: data.userId ?? null,
            giftsDisabled: false
          });
        } catch (err: unknown) {
          // SILENT-CATCH: Validation errors map to UI status; no telemetry needed.
          if (cancelled) return;
          // Backend codes are GIFT_-prefixed (`@morfi/api-errors`); matching the
          // unprefixed names silently dropped every validation case to
          // `network_error` and the inline message vanished (MOR-677).
          const code = extractErrorCode(err);
          const status: MorfiHandleStatus = code === 'GIFT_SELF_GIFT_BLOCKED' ? 'self' : code === 'GIFT_RECIPIENT_OPTED_OUT' ? 'opted_out' : code === 'GIFT_NOT_FOUND' ? 'not_found' : code === 'GIFT_INVALID_HANDLE_FORMAT' ? 'invalid_format' : 'network_error';
          setChecked({
            handle: trimmed,
            status,
            recipientUserId: null,
            giftsDisabled: false
          });
        }
      })();
    }, DEBOUNCE_MS);
    return (): void => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [trimmed, mutateAsync]);
  if (trimmed.length === 0) {
    return {
      status: 'idle',
      recipientUserId: null,
      giftsDisabled: false
    };
  }
  if (!HANDLE_REGEX.test(trimmed)) {
    return {
      status: 'invalid_format',
      recipientUserId: null,
      giftsDisabled: false
    };
  }
  if (checked?.handle !== trimmed) {
    return {
      status: 'checking',
      recipientUserId: null,
      giftsDisabled: false
    };
  }
  return checked;
}